/**
 * Precio de las líneas del carrito Vue, cupones de carrito y "Ahorrás $X"
 * arriba del total, y
 * corrección de cantidades/borrado cuando un descuento separa el mismo
 * producto en dos líneas.
 *
 * El componente LineItem del bundle Vue arma el precio con texto fijo
 * ("$46.800 - CUPÓN: Gratis - $0"), aunque el descuento sea $0. Acá se dibuja
 * un bloque propio con los datos reales de /cart.js y el de Vue se oculta por
 * CSS (assets/component-cart-lines.css):
 *  - con descuento: precio original tachado y precio final al lado;
 *  - abajo, cada cupón que descuenta algo en esa línea, con su monto;
 *  - regalos: tag REGALO en vez de "Gratis - $0".
 *
 * Cada .cart-item se empareja con su línea por variante y orden de aparición
 * (Vue las dibuja en el mismo orden que /cart.js).
 */
(() => {
  const watch = window.TCLCartWatch;
  const P = window.TCLProduct;
  if (!watch || !P || window.TCLCartLines) return;

  const { escapeHtml, money } = P;
  const T = Object.assign({ gift: 'Regalo', savings: 'Ahorrás' }, window.TCL_CART_LINES_STRINGS);

  const discountsOf = (item) =>
    (item.line_level_discount_allocations || [])
      .filter((d) => d.amount > 0)
      .map((d) => ({ title: d.discount_application?.title || '', amount: d.amount }));

  const priceHtml = (item) => {
    const isGift = !!(item.properties?._isGift || item.properties?._isGiftMulti);
    const original = item.original_line_price;
    const final = item.final_line_price;
    const discounts = discountsOf(item);
    const current = final === 0 && (isGift || discounts.length) ? `<span class="tcl-line-price__gift">${escapeHtml(T.gift)}</span>` : `<strong>${escapeHtml(money(final))}</strong>`;
    return `
      <div class="tcl-line-price__amounts">
        ${final < original ? `<s>${escapeHtml(money(original))}</s>` : ''}
        ${current}
      </div>
      ${discounts
        .map(
          (d) =>
            `<span class="tcl-line-price__discount"><span>${escapeHtml(d.title)}</span><span>-${escapeHtml(money(d.amount))}</span></span>`
        )
        .join('')}`;
  };

  const render = () => {
    const cart = watch.cart;
    const root = document.getElementById('ModalCart');
    if (!cart || !root) return;

    // Líneas: k-ésimo .cart-item de una variante ↔ k-ésima línea de esa variante.
    const seen = {};
    root.querySelectorAll('.cart-item[data-variant-id]').forEach((el) => {
      const variantId = Number(el.dataset.variantId);
      const n = (seen[variantId] = (seen[variantId] || 0) + 1);
      const item = cart.items.filter((i) => i.variant_id === variantId)[n - 1];
      const holder = el.querySelector('.item--qty');
      if (!item || !holder) return;

      let block = holder.querySelector(':scope > .tcl-line-price');
      if (!block) {
        block = document.createElement('div');
        block.className = 'tcl-line-price';
        holder.appendChild(block);
      }
      const html = priceHtml(item);
      if (block.dataset.html !== html) {
        block.dataset.html = html;
        block.innerHTML = html;
      }
      // Solo si falta: agregarla igual dispara el observer y haría un bucle.
      if (!el.classList.contains('tcl-line')) el.classList.add('tcl-line');
    });

    // Arriba del total: cupones a nivel carrito (ej. "10% en todo el pedido",
    // que no aparecen en ninguna línea) y "Ahorrás $X".
    const footer = root.querySelector('.cart--footer');
    if (footer) {
      let summary = footer.querySelector(':scope > .tcl-cart-summary');
      if (!summary) {
        summary = document.createElement('div');
        summary.className = 'tcl-cart-summary';
        footer.prepend(summary);
      }
      const cartDiscounts = (cart.cart_level_discount_applications || []).filter((d) => d.total_allocated_amount > 0);
      const html =
        cartDiscounts
          .map(
            (d) =>
              `<p class="tcl-cart-summary__discount"><span>${escapeHtml(d.title)}</span><span>-${escapeHtml(money(d.total_allocated_amount))}</span></p>`
          )
          .join('') +
        (cart.total_discount > 0
          ? `<p class="tcl-cart-summary__savings">${escapeHtml(T.savings)} <strong>${escapeHtml(money(cart.total_discount))}</strong></p>`
          : '');
      if (summary.dataset.html !== html) {
        summary.dataset.html = html;
        summary.innerHTML = html;
        summary.hidden = !html;
      }
    }

    // El carrito abierto queda por encima del botón de WhatsApp (ver CSS).
    document.documentElement.classList.toggle('tcl-cart-open', !!root.querySelector('.cart.is--open'));
  };

  // --- Líneas repetidas de la misma variante ------------------------------
  // Un descuento tipo 4x3 hace que Shopify separe el mismo producto en dos
  // líneas (una con el descuento). El carrito Vue cambia cantidades y borra
  // por variante (/cart/change.js con id = variant id), así que con dos líneas
  // de la misma variante el +, el − y el tacho actúan sobre la línea
  // equivocada o sobre todas. Se recuerda qué línea se tocó y, si su variante
  // está en más de una línea, el pedido se reescribe con la key de esa línea.

  const lineFor = (el) => {
    const cart = watch.cart;
    const root = document.getElementById('ModalCart');
    if (!cart || !root || !el) return null;
    const variantId = Number(el.dataset.variantId);
    const sameVariant = [...root.querySelectorAll(`.cart-item[data-variant-id="${variantId}"]`)];
    const lines = cart.items.filter((i) => i.variant_id === variantId);
    if (lines.length < 2) return null;
    return lines[sameVariant.indexOf(el)] || null;
  };

  let touched = null;
  const remember = (e) => {
    const el = e.target.closest?.('#ModalCart .cart-item[data-variant-id]');
    if (el) touched = { el, at: Date.now() };
  };
  document.addEventListener('pointerdown', remember, true);
  document.addEventListener('click', remember, true);

  const CART_CHANGE = /\/cart\/change(\.js)?(\?|$)/;
  const nextFetch = window.fetch;
  window.fetch = function (resource, options) {
    try {
      const url = typeof resource === 'string' ? resource : resource?.url || '';
      if (CART_CHANGE.test(url) && touched && Date.now() - touched.at < 3000 && typeof options?.body === 'string') {
        const body = JSON.parse(options.body);
        const line = /^\d+$/.test(String(body.id)) ? lineFor(touched.el) : null;
        if (line && String(line.variant_id) === String(body.id)) {
          body.id = line.key;
          options = { ...options, body: JSON.stringify(body) };
        }
      }
    } catch (e) {
      /* si algo falla, el pedido sale como estaba */
    }
    return nextFetch.call(this, resource, options);
  };

  // Vue vuelve a dibujar las líneas después de cada cambio: se re-aplica
  // cuando cambia el carrito y cuando cambia el DOM del carrito.
  let frame;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(render);
  };

  watch.subscribe(schedule);

  const start = () => {
    const root = document.getElementById('ModalCart');
    if (!root) return;
    // attributes: el carrito se abre y cierra con la clase is--open.
    new MutationObserver(schedule).observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class'] });
    schedule();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.TCLCartLines = { render };
})();
